import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useSetAddOnMutation } from "@/store/rest_api";
import { AddOnType } from "@/model/interface/AddOn";
import { cn } from "@/lib/utils";
import { formatAddOnPrice } from "@/util/addOns";
import { apiErrorMessage } from "@/util/apiError";

const BUNDLES = [
  { addOnType: AddOnType.PROPERTY_DATA_5K, leads: 400 },
  { addOnType: AddOnType.PROPERTY_DATA_10K, leads: 1350 },
];

interface PropertyDataDialogProps {
  activeTier: AddOnType | null;
  canceling: boolean;
  smallPriceCents: number;
  largePriceCents: number;
  endDate: string | null;
  onClose: () => void;
}

export function PropertyDataDialog({
  activeTier,
  canceling,
  smallPriceCents,
  largePriceCents,
  endDate,
  onClose,
}: PropertyDataDialogProps) {
  const [setAddOn, { isLoading: isSaving }] = useSetAddOnMutation();
  const [selected, setSelected] = useState<AddOnType>(activeTier ?? AddOnType.PROPERTY_DATA_5K);
  const [confirmingTurnOff, setConfirmingTurnOff] = useState(false);

  const endsOn = endDate ?? "your billing date";
  const isSwitch = !!activeTier && !canceling && selected !== activeTier;

  const priceFor = (addOnType: AddOnType) =>
    addOnType === AddOnType.PROPERTY_DATA_10K ? largePriceCents : smallPriceCents;

  const leadsFor = (addOnType: AddOnType) =>
    (BUNDLES.find((bundle) => bundle.addOnType === addOnType)?.leads ?? 0).toLocaleString("en-US");

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const applyBundle = async (bundle: AddOnType | null, message: string) => {
    try {
      for (const tier of BUNDLES) {
        await setAddOn({ addOnType: tier.addOnType, quantity: tier.addOnType === bundle ? 1 : 0 }).unwrap();
      }
      toast.success(message);
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update property data"));
    }
  };

  const summary = () => {
    if (!activeTier) {
      return `Starts today. The rest of this billing period is prorated onto your next invoice, then ${formatAddOnPrice(priceFor(selected))} a month.`;
    }
    if (isSwitch) {
      return `${leadsFor(selected)} leads start today, prorated onto your next invoice. Your ${leadsFor(activeTier)} leads keep working until ${endsOn}, with no credit.`;
    }
    return null;
  };

  const summaryText = summary();

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto text-foreground">
        <DialogHeader>
          <DialogTitle>{activeTier ? "Property data" : "Add property data"}</DialogTitle>
          <DialogDescription>Skip-traced leads delivered to your account every month.</DialogDescription>
        </DialogHeader>

        {canceling ? (
          <div className="rounded-lg bg-muted p-3 text-sm">
            <p className="font-semibold">{endDate ? `Ends ${endDate}` : "Ends at your billing date"}</p>
            <p className="text-muted-foreground">
              Your {activeTier ? leadsFor(activeTier) : ""} leads keep coming until then. Keep property data to carry on
              with no new charge.
            </p>
          </div>
        ) : (
          <RadioGroup
            value={selected}
            onValueChange={(value) => setSelected(value as AddOnType)}
            className="space-y-2"
          >
            {BUNDLES.map((bundle) => (
              <Label
                key={bundle.addOnType}
                htmlFor={`bundle-${bundle.addOnType}`}
                className={cn(
                  "flex cursor-pointer items-center gap-3 rounded-lg border p-3 font-normal",
                  selected === bundle.addOnType && "border-primary bg-primary/5",
                )}
              >
                <RadioGroupItem value={bundle.addOnType} id={`bundle-${bundle.addOnType}`} />
                <span className="flex-1">
                  <span className="block text-sm font-semibold">{bundle.leads.toLocaleString("en-US")} leads a month</span>
                  <span className="block text-xs text-muted-foreground">
                    About ${(priceFor(bundle.addOnType) / bundle.leads / 100).toFixed(2)} a lead
                    {activeTier === bundle.addOnType ? ", your current bundle" : ""}
                  </span>
                </span>
                <span className="text-sm tabular-nums">
                  <span className="font-semibold">{formatAddOnPrice(priceFor(bundle.addOnType))}</span>
                  <span className="text-muted-foreground">/mo</span>
                </span>
              </Label>
            ))}
          </RadioGroup>
        )}

        <p className="text-xs text-muted-foreground">Leads beyond your monthly amount cost $0.13 each, paid from your balance.</p>

        {summaryText && <p className="rounded-lg bg-muted p-3 text-sm tabular-nums">{summaryText}</p>}

        {confirmingTurnOff ? (
          <div className="space-y-3 rounded-lg border border-destructive/40 p-3">
            <p className="text-sm">Property data keeps working until {endsOn}, then it won't renew.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirmingTurnOff(false)}>
                Keep it
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={isSaving}
                onClick={() => applyBundle(null, `Property data stops renewing after ${endsOn}`)}
              >
                Turn off property data
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2">
            {activeTier && !canceling ? (
              <Button
                variant="ghost"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => setConfirmingTurnOff(true)}
              >
                Turn off property data
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                {activeTier && !isSwitch && !canceling ? "Done" : "Cancel"}
              </Button>
              {canceling && activeTier && (
                <Button disabled={isSaving} onClick={() => applyBundle(activeTier, "Property data will keep renewing")}>
                  Keep property data
                </Button>
              )}
              {!activeTier && (
                <Button disabled={isSaving} onClick={() => applyBundle(selected, "Property data added")}>
                  Add property data
                </Button>
              )}
              {isSwitch && (
                <Button
                  disabled={isSaving}
                  onClick={() => applyBundle(selected, `Switched to ${leadsFor(selected)} leads a month`)}
                >
                  Switch to {leadsFor(selected)} leads
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
